import axios, { AxiosHeaders } from 'axios';
import { createThrottledAdapter } from './request-throttle';
import {
    clearAuthSession,
    getAccessToken,
    getRefreshToken,
    isAccessTokenExpired,
    saveTokensFromRefresh,
} from '../../features/auth/utils/token-storage';
import { store } from '../../store';
import { LOGOUT_ACTION } from 'store/legacyActionsBridge';

const apiBaseUrl =
    (process.env.REACT_APP_API_BASE_URL ?? '').trim() ||
    'http://localhost:3000';

const axiosInstance = axios.create({
    baseURL: apiBaseUrl,
});

/**
 * ── ISTEK KISITLAMA (bkz. `request-throttle.ts`) ────────────────────────────
 *
 * Iki sey yapar:
 *   1. AYNI ANDA ucan AYNI GET'i TEKLER. `/company-list` acilisinda
 *      `participation-status` ve `businesses?mine=true` ikiser kez
 *      cikiyordu — tekrar BILEREK var (bkz. `jsx/index.tsx` kilitlenme
 *      aciklamasi), o yuzden cagri yerleri degil bu katman duzeltiyor.
 *   2. Es zamanli istek sayisina tavan koyar. API HTTP/2 konusuyor, yani
 *      tarayicinin HTTP/1.1'deki "host basina 6 baglanti" freni YOK;
 *      onlarca istek gercekten ayni anda cikip edge nginx'in
 *      `limit_req zone=api` esigini tetikliyordu.
 *
 * ⚠️ `defaults.adapter` bir AD LISTESI (`['xhr','http','fetch']`), fonksiyon
 *    degil. Gercek fonksiyonu `axios.getAdapter` cozer — dogrudan cagrilmaya
 *    calisilirsa "adapter is not a function" alinir.
 */
axiosInstance.defaults.adapter = createThrottledAdapter(
    axios.getAdapter(axiosInstance.defaults.adapter),
);

function performLogout() {
    clearAuthSession();
    store.dispatch({ type: LOGOUT_ACTION });
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
    }
}

type RefreshResult = {
    success: boolean;
    accessToken?: string;
    reason?: 'network_error' | 'invalid_token';
};

let refreshPromise: Promise<RefreshResult> | null = null;

async function attemptTokenRefresh(): Promise<RefreshResult> {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async (): Promise<RefreshResult> => {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
            return { success: false, reason: 'invalid_token' };
        }
        try {
            const response = await axios.post(`${apiBaseUrl}/auth/refresh`, { refreshToken });
            const data = response.data?.data ?? response.data;
            const newAccessToken: string | undefined = data?.accessToken;
            if (!newAccessToken) {
                return { success: false, reason: 'invalid_token' };
            }
            saveTokensFromRefresh({
                accessToken: newAccessToken,
                refreshToken: data?.refreshToken,
                expiresAt: data?.expiresAt,
            });
            return { success: true, accessToken: newAccessToken };
        } catch (err: any) {
            const status = err?.response?.status as number | undefined;
            if (status === 401 || status === 403) {
                return { success: false, reason: 'invalid_token' };
            }
            return { success: false, reason: 'network_error' };
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

/** Token'ı süresi dolmadan yeniler (ör. açık kalan B2B anlık takip ekranı). */
export const refreshAccessTokenNow = () => attemptTokenRefresh();

axiosInstance.interceptors.request.use(async (config) => {
    const token = getAccessToken();
    const skipLogout = (config as any).skipAuthRedirect === true;

    if (token && isAccessTokenExpired(token) && !skipLogout) {
        const result = await attemptTokenRefresh();
        if (result.success && result.accessToken) {
            config.headers = config.headers ?? new AxiosHeaders();
            (config.headers as AxiosHeaders).set('authorization', `Bearer ${result.accessToken}`);
            (config.headers as AxiosHeaders).set('accept', 'application/json');
            return config;
        }
        if (result.reason === 'invalid_token') {
            performLogout();
            return Promise.reject(new Error('Session expired'));
        }
        // network_error: devam et, server 401 dönerse response interceptor yakalar
    }

    config.headers = config.headers ?? new AxiosHeaders();
    if (token) {
        (config.headers as AxiosHeaders).set('authorization', `Bearer ${token}`);
    }
    (config.headers as AxiosHeaders).set('accept', 'application/json');
    return config;
});

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const skipLogout = error?.config?.skipAuthRedirect === true;
        const isUnauthorized = error?.response?.status === 401;
        const originalConfig = error?.config;

        if (!skipLogout && isUnauthorized && originalConfig && !originalConfig._retried) {
            originalConfig._retried = true;
            const result = await attemptTokenRefresh();
            if (result.success && result.accessToken) {
                originalConfig.headers = originalConfig.headers ?? {};
                originalConfig.headers.authorization = `Bearer ${result.accessToken}`;
                return axiosInstance(originalConfig);
            }
            if (result.reason === 'invalid_token') {
                performLogout();
            }
            // network_error: orijinal hatayı dön
        }

        return Promise.reject(error);
    },
);

export default axiosInstance;
